import React, { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import { AUTOPILOT_COLORS, autopilotCardStyle } from "../../../components/autopilot/styles";
import { useToast } from "../../../hooks/toast";
import { renderControl } from "../../Empresa/Parametros/renderControl";
import {
  atualizarCampo,
  atualizarConector,
  criarCampo,
  criarConector,
  getCampos,
  getConfig,
  getTiposProtocolo,
  getTiposValidacao,
  listarConectores,
  salvarConfig,
} from "../../../services/conectorCatalogoApi";
import "./ConectoresCatalogo.css";

// Catálogo de Conectores — CRUD admin Everlog (PO1-2560/2561). Lista de conectores +
// config global (ConectorParametro) editável + definições de campo (Parametro/UiConfig).
const heroCardStyle = { ...autopilotCardStyle, background: "var(--everlog-card-hero-bg)" };
const cardStyle = { ...autopilotCardStyle, background: "var(--everlog-card-hero-bg)", marginTop: "1.25rem" };
const labelStyle = { color: AUTOPILOT_COLORS.muted, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: "0.25rem" };
const thStyle = { color: AUTOPILOT_COLORS.muted, fontSize: "0.74rem", textTransform: "uppercase", letterSpacing: "0.04em", borderBottom: `1px solid ${AUTOPILOT_COLORS.border}` };
const sectionTitleStyle = { color: AUTOPILOT_COLORS.title, fontSize: "1rem", fontWeight: 700, margin: "0 0 0.75rem" };

const TIPOS_CAMPO = ["text", "password", "toggle", "number", "select", "url", "time"];
const CLASSIFICACOES = [
  { value: "CONECTOR", label: "CONECTOR (config global)" },
  { value: "EMBARCTRANS", label: "EMBARCTRANS (credencial por transportadora)" },
  // PO1-3613: parâmetro do vínculo do embarcador com o conector de ERP.
  { value: "EMBARCCONECTOR", label: "EMBARCCONECTOR (parâmetro do vínculo do embarcador)" },
];

// Lista padronizada da H1 (PO1-3613). Categoria = tipo de sistema; as
// transportadoras migraram de 'cotacao' para 'transportadora'.
const CATEGORIAS = ["erp", "fiscal", "captura", "transportadora", "coleta"];
const ESCOPOS = ["transportadora", "embarcador"];

const CONECTOR_VAZIO = {
  id: null,
  conectorKey: "",
  versao: "1.0",
  nome: "",
  categoria: "transportadora",
  escopo: "transportadora",
  direcao: "consulta",
  idTipoProtocolo: "",
  idTipoValidacao: "",
  status: true,
};
const CAMPO_VAZIO = { idParametro: null, nome: "", descricao: "", classificacao: "EMBARCTRANS", tipo: "text", placeholder: "", opcoes: "" };

export default function ConectoresCatalogo() {
  const { addToast } = useToast();

  const [loading, setLoading] = useState(false);
  const [conectores, setConectores] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [tiposProtocolo, setTiposProtocolo] = useState([]);
  const [tiposValidacao, setTiposValidacao] = useState([]);

  const [config, setConfig] = useState([]);
  const [configValores, setConfigValores] = useState({});
  const [savingConfig, setSavingConfig] = useState(false);

  const [campos, setCampos] = useState([]);

  const [formConector, setFormConector] = useState(null); // objeto p/ criar/editar
  const [savingConector, setSavingConector] = useState(false);
  const [formCampo, setFormCampo] = useState(null);
  const [savingCampo, setSavingCampo] = useState(false);

  const flash = (type, description) =>
    addToast({ place: "tr", type, title: type === "success" ? "OK" : "Atenção", description });

  const carregarConectores = useCallback(async () => {
    setLoading(true);
    try {
      const [cons, tp, tv] = await Promise.all([
        listarConectores(),
        getTiposProtocolo(),
        getTiposValidacao(),
      ]);
      setConectores(cons || []);
      setTiposProtocolo(tp || []);
      setTiposValidacao(tv || []);
      setSelectedId((cur) => (cons || []).some((c) => c.id === cur) ? cur : (cons && cons[0] && cons[0].id) || null);
    } catch (e) {
      flash("danger", e.response?.data?.error || "Falha ao carregar o catálogo (acesso admin?).");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    carregarConectores();
  }, [carregarConectores]);

  const selecionado = conectores.find((c) => c.id === selectedId) || null;

  const carregarDetalhe = useCallback(async (con) => {
    if (!con) return;
    try {
      const [cfg, cps] = await Promise.all([getConfig(con.id), getCampos(con.conectorKey)]);
      setConfig(cfg || []);
      const init = {};
      (cfg || []).forEach((c) => {
        init[c.idParametro] = c.tipo === "password" ? "" : c.valor == null ? "" : String(c.valor);
      });
      setConfigValores(init);
      setCampos(cps || []);
    } catch (e) {
      flash("danger", e.response?.data?.error || "Falha ao carregar o detalhe do conector.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selecionado) carregarDetalhe(selecionado);
    else {
      setConfig([]);
      setConfigValores({});
      setCampos([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  // ── Config global ─────────────────────────────────────────────────────────
  const salvarConfigGlobal = async () => {
    if (!selecionado) return;
    setSavingConfig(true);
    try {
      const valores = config.map((c) => ({ idParametro: c.idParametro, valor: configValores[c.idParametro] }));
      await salvarConfig(selecionado.id, valores);
      flash("success", "Configuração salva.");
      carregarDetalhe(selecionado);
    } catch (e) {
      flash("danger", e.response?.data?.error || "Falha ao salvar a configuração.");
    } finally {
      setSavingConfig(false);
    }
  };

  // ── Conector (criar/editar) ───────────────────────────────────────────────
  const salvarFormConector = async () => {
    const d = formConector;
    if (!d.conectorKey.trim() || !d.versao.trim()) {
      return flash("danger", "ConectorKey e Versão são obrigatórios.");
    }
    setSavingConector(true);
    try {
      const payload = {
        conectorKey: d.conectorKey.trim(),
        versao: d.versao.trim(),
        nome: d.nome,
        categoria: d.categoria,
        escopo: d.escopo,
        direcao: d.direcao,
        idTipoProtocolo: d.idTipoProtocolo || null,
        idTipoValidacao: d.idTipoValidacao || null,
        status: d.status ? 1 : 0,
      };
      if (d.id) await atualizarConector(d.id, payload);
      else await criarConector(payload);
      flash("success", d.id ? "Conector atualizado." : "Conector criado.");
      setFormConector(null);
      carregarConectores();
    } catch (e) {
      flash("danger", e.response?.data?.error || "Falha ao salvar o conector.");
    } finally {
      setSavingConector(false);
    }
  };

  // ── Campo (criar/editar) ──────────────────────────────────────────────────
  const salvarFormCampo = async () => {
    const d = formCampo;
    if (!d.nome.trim()) return flash("danger", "Nome do campo é obrigatório.");
    setSavingCampo(true);
    try {
      const payload = {
        nome: d.nome.trim(),
        descricao: d.descricao,
        classificacao: d.classificacao,
        tipo: d.tipo,
        placeholder: d.placeholder,
        opcoes: d.opcoes,
      };
      if (d.idParametro) await atualizarCampo(d.idParametro, payload);
      else await criarCampo(selecionado.conectorKey, payload);
      flash("success", d.idParametro ? "Campo atualizado." : "Campo criado.");
      setFormCampo(null);
      carregarDetalhe(selecionado);
    } catch (e) {
      flash("danger", e.response?.data?.error || "Falha ao salvar o campo.");
    } finally {
      setSavingCampo(false);
    }
  };

  return (
    <div className="content">
      <Card style={heroCardStyle}>
        <CardHeader style={{ background: "transparent", border: "none", padding: "1.5rem 1.65rem 1.25rem" }}>
          <Row className="align-items-start">
            <Col>
              <div style={{ color: AUTOPILOT_COLORS.accent, fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 700, marginBottom: "0.5rem" }}>
                Admin Everlog
              </div>
              <h2 style={{ color: AUTOPILOT_COLORS.title, fontSize: "1.9rem", fontWeight: 800, margin: "0 0 0.5rem" }}>
                Catálogo de Conectores
              </h2>
              <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.95rem", maxWidth: 820, lineHeight: 1.6 }}>
                Gerencie os conectores de integração (SSW, Rodonaves, Quality, Correios…), a config
                global de cada um (URL, timeout — sem deploy) e os campos que aparecem na tela do embarcador.
              </div>
            </Col>
            <Col xs="auto">
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", justifyContent: "flex-end" }}>
                <Button className="btn-sm" color="secondary" outline onClick={carregarConectores} disabled={loading}>
                  {loading ? <Spinner size="sm" /> : "↺ Atualizar"}
                </Button>
                <Button color="info" className="btn-sm" onClick={() => setFormConector({ ...CONECTOR_VAZIO })}>
                  <i className="tim-icons icon-simple-add" /> Novo conector
                </Button>
              </div>
            </Col>
          </Row>
        </CardHeader>
      </Card>

      <Row>
        <Col lg="5">
          <Card style={cardStyle}>
            <CardBody>
              <div style={sectionTitleStyle}>Conectores</div>
              <div style={{ overflowX: "auto" }}>
                <Table className="text-left" style={{ color: AUTOPILOT_COLORS.text }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Key / Versão</th>
                      <th style={thStyle}>Protocolo</th>
                      <th style={thStyle}>Validação</th>
                      <th style={thStyle}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {conectores.map((c) => (
                      <tr
                        key={c.id}
                        onClick={() => setSelectedId(c.id)}
                        style={{
                          cursor: "pointer",
                          background: c.id === selectedId ? AUTOPILOT_COLORS.insetSurfaceActive : "transparent",
                        }}
                      >
                        <td>
                          <div style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600 }}>{c.conectorKey} {c.versao}</div>
                          <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.78rem" }}>{c.nome || "-"}</div>
                        </td>
                        <td>{c.protocolo || "-"}</td>
                        <td>{c.validacao || "-"}</td>
                        <td>
                          <Badge color={c.status ? "success" : "secondary"} pill>
                            {c.status ? "Ativo" : "Inativo"}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                    {conectores.length === 0 && !loading && (
                      <tr>
                        <td colSpan={4} style={{ color: AUTOPILOT_COLORS.muted, padding: "1rem" }}>
                          Nenhum conector no catálogo.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col lg="7">
          <Card style={cardStyle}>
            <CardBody>
              {!selecionado ? (
                <div style={{ color: AUTOPILOT_COLORS.muted, padding: "1rem" }}>
                  Selecione um conector para ver e editar a configuração e os campos.
                </div>
              ) : (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                    <div>
                      <div style={{ color: AUTOPILOT_COLORS.title, fontSize: "1.1rem", fontWeight: 700 }}>
                        {selecionado.conectorKey} {selecionado.versao}
                      </div>
                      <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.82rem" }}>
                        {selecionado.categoria} · {selecionado.escopo} · {selecionado.protocolo} · {selecionado.validacao}
                      </div>
                    </div>
                    <Button size="sm" color="secondary" outline onClick={() => setFormConector({
                      ...selecionado,
                      status: !!selecionado.status,
                      idTipoProtocolo: selecionado.idTipoProtocolo || "",
                      idTipoValidacao: selecionado.idTipoValidacao || "",
                    })}>
                      Editar conector
                    </Button>
                  </div>

                  {/* Config global */}
                  <div style={sectionTitleStyle}>Configuração global</div>
                  {config.length === 0 ? (
                    <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.84rem", marginBottom: "1rem" }}>
                      Sem parâmetros de config (Classificacao CONECTOR) para este conector.
                    </div>
                  ) : (
                    <div style={{ marginBottom: "0.75rem" }}>
                      {config.map((c) => (
                        <div key={c.idParametro} style={{ marginBottom: "0.8rem" }}>
                          <div style={labelStyle}>{c.descricao || c.nome}</div>
                          {renderControl({
                            param: { Id: c.idParametro, Tipo: c.tipo, Placeholder: c.placeholder },
                            valor: configValores[c.idParametro],
                            onChange: (v) => setConfigValores((p) => ({ ...p, [c.idParametro]: v })),
                            saving: savingConfig,
                            fullWidth: true,
                          })}
                        </div>
                      ))}
                      <Button color="success" size="sm" onClick={salvarConfigGlobal} disabled={savingConfig}>
                        {savingConfig ? <Spinner size="sm" /> : "Salvar configuração"}
                      </Button>
                    </div>
                  )}

                  {/* Campos */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "1.5rem" }}>
                    <div style={sectionTitleStyle}>Campos ({`conector-${selecionado.conectorKey}`})</div>
                    <Button size="sm" color="info" outline onClick={() => setFormCampo({ ...CAMPO_VAZIO })}>
                      <i className="tim-icons icon-simple-add" /> Novo campo
                    </Button>
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <Table className="text-left" style={{ color: AUTOPILOT_COLORS.text }}>
                      <thead>
                        <tr>
                          <th style={thStyle}>Nome</th>
                          <th style={thStyle}>Classificação</th>
                          <th style={thStyle}>Tipo</th>
                          <th style={thStyle} />
                        </tr>
                      </thead>
                      <tbody>
                        {campos.map((c) => (
                          <tr key={c.idParametro}>
                            <td>
                              <div style={{ color: AUTOPILOT_COLORS.title }}>{c.nome}</div>
                              <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.76rem" }}>{c.descricao || ""}</div>
                            </td>
                            <td>
                              <Badge color={c.classificacao === "CONECTOR" ? "info" : "warning"} pill>
                                {c.classificacao}
                              </Badge>
                            </td>
                            <td>{c.tipo || "-"}</td>
                            <td style={{ textAlign: "right" }}>
                              <Button size="sm" color="secondary" outline onClick={() => setFormCampo({
                                idParametro: c.idParametro,
                                nome: c.nome,
                                descricao: c.descricao || "",
                                classificacao: c.classificacao || "EMBARCTRANS",
                                tipo: c.tipo || "text",
                                placeholder: c.placeholder || "",
                                opcoes: c.opcoes || "",
                              })}>
                                Editar
                              </Button>
                            </td>
                          </tr>
                        ))}
                        {campos.length === 0 && (
                          <tr>
                            <td colSpan={4} style={{ color: AUTOPILOT_COLORS.muted, padding: "0.75rem" }}>
                              Nenhum campo definido.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Modal conector */}
      <Modal isOpen={!!formConector} toggle={() => setFormConector(null)} modalClassName="modal-align-top" contentClassName="cc-modal" fade={false}>
        <ModalBody style={{ padding: "1.5rem" }}>
          <div style={{ color: AUTOPILOT_COLORS.title, fontWeight: 700, marginBottom: "1rem" }}>
            {formConector && formConector.id ? "Editar conector" : "Novo conector"}
          </div>
          {formConector && (
            <>
              <Row>
                <Col md="6">
                  <Label style={labelStyle}>ConectorKey</Label>
                  <Input value={formConector.conectorKey} disabled={!!formConector.id}
                    onChange={(e) => setFormConector({ ...formConector, conectorKey: e.target.value })} />
                </Col>
                <Col md="6">
                  <Label style={labelStyle}>Versão</Label>
                  <Input value={formConector.versao} disabled={!!formConector.id}
                    onChange={(e) => setFormConector({ ...formConector, versao: e.target.value })} />
                </Col>
              </Row>
              <Label style={{ ...labelStyle, marginTop: "0.75rem" }}>Nome</Label>
              <Input value={formConector.nome} onChange={(e) => setFormConector({ ...formConector, nome: e.target.value })} />
              <Row style={{ marginTop: "0.75rem" }}>
                <Col md="4">
                  <Label style={labelStyle}>Categoria</Label>
                  <Input type="select" value={formConector.categoria || ""} onChange={(e) => setFormConector({ ...formConector, categoria: e.target.value })}>
                    {formConector.categoria && CATEGORIAS.indexOf(formConector.categoria) === -1 ? <option value={formConector.categoria}>{formConector.categoria} (fora da lista)</option> : null}
                    {CATEGORIAS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </Input>
                </Col>
                <Col md="4">
                  <Label style={labelStyle}>Escopo</Label>
                  <Input type="select" value={formConector.escopo || ""} onChange={(e) => setFormConector({ ...formConector, escopo: e.target.value })}>
                    {formConector.escopo && ESCOPOS.indexOf(formConector.escopo) === -1 ? <option value={formConector.escopo}>{formConector.escopo} (fora da lista)</option> : null}
                    {ESCOPOS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </Input>
                </Col>
                <Col md="4">
                  <Label style={labelStyle}>Direção</Label>
                  <Input value={formConector.direcao} onChange={(e) => setFormConector({ ...formConector, direcao: e.target.value })} />
                </Col>
              </Row>
              <Row style={{ marginTop: "0.75rem" }}>
                <Col md="6">
                  <Label style={labelStyle}>Protocolo</Label>
                  <Input type="select" value={formConector.idTipoProtocolo}
                    onChange={(e) => setFormConector({ ...formConector, idTipoProtocolo: e.target.value })}>
                    <option value="">—</option>
                    {tiposProtocolo.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
                  </Input>
                </Col>
                <Col md="6">
                  <Label style={labelStyle}>Validação</Label>
                  <Input type="select" value={formConector.idTipoValidacao}
                    onChange={(e) => setFormConector({ ...formConector, idTipoValidacao: e.target.value })}>
                    <option value="">—</option>
                    {tiposValidacao.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
                  </Input>
                </Col>
              </Row>
              <div style={{ marginTop: "0.9rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <Input type="checkbox" checked={!!formConector.status} style={{ position: "static", margin: 0 }}
                  onChange={(e) => setFormConector({ ...formConector, status: e.target.checked })} />
                <span style={{ color: AUTOPILOT_COLORS.text, fontSize: "0.85rem" }}>Ativo</span>
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setFormConector(null)} disabled={savingConector}>Cancelar</Button>
          <Button color="info" onClick={salvarFormConector} disabled={savingConector}>
            {savingConector ? <Spinner size="sm" /> : "Salvar"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal campo */}
      <Modal isOpen={!!formCampo} toggle={() => setFormCampo(null)} modalClassName="modal-align-top" contentClassName="cc-modal" fade={false}>
        <ModalBody style={{ padding: "1.5rem" }}>
          <div style={{ color: AUTOPILOT_COLORS.title, fontWeight: 700, marginBottom: "1rem" }}>
            {formCampo && formCampo.idParametro ? "Editar campo" : "Novo campo"}
          </div>
          {formCampo && (
            <>
              <Label style={labelStyle}>Nome (chave)</Label>
              <Input value={formCampo.nome} disabled={!!formCampo.idParametro}
                onChange={(e) => setFormCampo({ ...formCampo, nome: e.target.value })} />
              <Label style={{ ...labelStyle, marginTop: "0.75rem" }}>Descrição (rótulo na tela)</Label>
              <Input value={formCampo.descricao} onChange={(e) => setFormCampo({ ...formCampo, descricao: e.target.value })} />
              <Row style={{ marginTop: "0.75rem" }}>
                <Col md="6">
                  <Label style={labelStyle}>Classificação</Label>
                  <Input type="select" value={formCampo.classificacao} disabled={!!formCampo.idParametro}
                    onChange={(e) => setFormCampo({ ...formCampo, classificacao: e.target.value })}>
                    {CLASSIFICACOES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </Input>
                </Col>
                <Col md="6">
                  <Label style={labelStyle}>Tipo (UI)</Label>
                  <Input type="select" value={formCampo.tipo}
                    onChange={(e) => setFormCampo({ ...formCampo, tipo: e.target.value })}>
                    {TIPOS_CAMPO.map((t) => <option key={t} value={t}>{t}</option>)}
                  </Input>
                </Col>
              </Row>
              <Label style={{ ...labelStyle, marginTop: "0.75rem" }}>Placeholder</Label>
              <Input value={formCampo.placeholder} onChange={(e) => setFormCampo({ ...formCampo, placeholder: e.target.value })} />
              {formCampo.tipo === "select" && (
                <>
                  <Label style={{ ...labelStyle, marginTop: "0.75rem" }}>Opções (JSON ou CSV)</Label>
                  <Input value={formCampo.opcoes} onChange={(e) => setFormCampo({ ...formCampo, opcoes: e.target.value })} />
                </>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setFormCampo(null)} disabled={savingCampo}>Cancelar</Button>
          <Button color="info" onClick={salvarFormCampo} disabled={savingCampo}>
            {savingCampo ? <Spinner size="sm" /> : "Salvar"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
